/**
 * Shared reward subtotal — the single number the reward stepper and the gift
 * engine both spend.
 *
 * Two properties matter and both are easy to get wrong separately:
 *
 *   POST-DISCOUNT.  `final_line_price` is what the line actually costs after
 *   every discount Shopify is currently applying — automatic discounts and any
 *   code applied to the cart. `original_line_price` and `cart.items_subtotal_price`
 *   are not the same thing and will drift from checkout the moment a promo runs.
 *
 *   GIFT-EXCLUDED.  An auto-added gift must never count toward the threshold
 *   that unlocked it, or a $59 cart tips itself over $60 and the reward
 *   oscillates on every cart update.
 *
 * Kept in its own file because two callers computing "the subtotal" separately
 * is how the bar and the gift end up disagreeing about whether a tier is met.
 * cart-gift.js falls back to its own inline copy if this file fails to load, so
 * a missing asset degrades the stepper rather than breaking the gift.
 *
 * Works on the /cart.js shape (item.properties is an object) — not the Liquid
 * cart drop, where properties are an array of pairs. Liquid does its own pass
 * in reward-stepper.liquid for the first paint.
 */
window.FBReward = window.FBReward || {};

(function () {
  const GIFT_PROPERTY = "_gift";

  /** Is this line an auto-added gift? Mirrors cart-gift.js isGift(). */
  function isGiftLine(item) {
    return !!(item && item.properties && item.properties[GIFT_PROPERTY] === "true");
  }

  /**
   * Post-discount, gift-excluded cart subtotal in cents.
   * @param {object} cart - parsed /cart.js response
   */
  function subtotalCents(cart) {
    const items = (cart && cart.items) || [];
    return items.reduce((sum, item) => {
      if (isGiftLine(item)) return sum;
      return sum + (item.final_line_price || 0);
    }, 0);
  }

  /** Same value in major units, for comparing against configured thresholds. */
  function subtotalMajor(cart) {
    return subtotalCents(cart) / 100;
  }

  /**
   * Where a subtotal sits ALONG THE RAIL, as a percentage, 0-100.
   *
   * Not the same thing as "subtotal over the top tier". The nodes are spaced
   * evenly by flex, so with tiers at $60 and $80 the $60 dot sits at the rail's
   * midpoint — but $60/$80 is 75%, and the fill sailed a quarter of the rail
   * past the dot it was supposed to be arriving at. Reported from the CA
   * preview on 17 Sept: "it reads 75% while its node sits at 50%".
   *
   * So interpolate between NODE POSITIONS instead: the cart node at 0, then one
   * segment per tier, and the position within a segment is how far the subtotal
   * has travelled between the two thresholds either side of it.
   *
   * Shared so the drawer rail and the announcement bar's strip can never
   * disagree about how far along the same cart is.
   *
   * @param {number} subtotal - major units, gift-excluded
   * @param {Array} tiers - objects carrying `amount`
   */
  function positionPct(subtotal, tiers) {
    const amounts = (tiers || [])
      .map((t) => Number(t && t.amount))
      .filter((a) => a > 0)
      .sort((a, b) => a - b);
    if (!amounts.length) return 0;

    const segments = amounts.length;
    if (subtotal >= amounts[segments - 1]) return 100;

    let lower = 0;
    for (let i = 0; i < segments; i++) {
      const upper = amounts[i];
      if (subtotal < upper) {
        const span = upper - lower;
        const within = span > 0 ? (subtotal - lower) / span : 0;
        return ((i + within) / segments) * 100;
      }
      lower = upper;
    }
    return 100;
  }

  window.FBReward.isGiftLine = isGiftLine;
  window.FBReward.subtotalCents = subtotalCents;
  window.FBReward.subtotalMajor = subtotalMajor;
  window.FBReward.positionPct = positionPct;
})();
